import moment from 'moment';
import api from 'services/api';
import { FaFilePdf, FaFileImage, FaFileExcel, FaFile } from 'react-icons/fa';
import {
  AnullSchema,
  AttachmentDescriptionSchema,
  ChangeResponsibleSchema,
  DescriptionSchema,
  validateAttachmentsSchema,
  EndLotOccurrenceSchema,
} from './validator';

import { FORMAT_DATETIME_REGEX, CPF_CNPJ_REGEX } from 'v3/utils/masks';

const VISIBLE_STATUSES = ['DEFAULT', 'REQUIRED', 'SELECTED'];
const REQUIRED_STATUS = 'REQUIRED';
const DELIVERY_ATTEMPT_SLUGS = [
  'FIRST_DELIVERY_ATTEMPT',
  'SECOND_DELIVERY_ATTEMPT',
];

export function getNextBusinessDay() {
  const next = moment().add(1, 'day');
  while (next.day() === 0 || next.day() === 6) {
    next.add(1, 'day');
  }
  return next
    .set({ hour: 18, minute: 0, second: 0 })
    .format('DD/MM/YYYY HH:mm');
}

export const showDocumentFieldsTypes = [
  'DELIVERY_LOCATION_NOT_FOUND',
  'DELIVERY_LOCATION_CLOSED',
  'SCHEDULED_DELIVERY',
  'DEPARTURE_FOR_DELIVERY',
  'DELIVERY',
  'DAMAGE',
  'MERCHANDISE_IN_REDISTRIBUTION',
  'TOTAL_RETURN',
  'ARRIVAL_AT_CLIENT_WAITING_UNLOADING',
  'IN_TRANSIT_TO_BRANCH',
  'ARRIVAL_AT_CARRIER',
  'REDELIVERY_COMPLETED',
  'PARTIAL_LOSS',
  'REFUSAL_DUE_TO_DELIVERY_DELAY',
  'TRIP_DELAY',
  'VEHICLE_BREAKDOWN',
  'STRIKE_PROTESTS',
  'RAIN_IN_REGION',
  'LOST_CARGO',
  'DAMAGED_CARGO',
  'FIRST_DELIVERY_ATTEMPT',
  'SECOND_DELIVERY_ATTEMPT',
  'RECIPIENT_ADDRESS_CHANGED',
];

export const attachmentRequired = [
  'ACCIDENT',
  'DAMAGE',
  'DELIVERY',
  'DOCUMENTATION',
];

export const descriptionNotRequiredSlug = [
  'DELIVERY',
  'DELIVERY_LOCATION_NOT_FOUND',
  'DELIVERY_LOCATION_CLOSED',
  'SCHEDULED_DELIVERY',
  'DEPARTURE_FOR_DELIVERY',
  'DAMAGE',
];

function normalizeFieldName(value = '') {
  return String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function parseDateTime(value = '') {
  return FORMAT_DATETIME_REGEX.test(value);
}

export function getOccurrenceFieldKey(fieldName = '') {
  const normalized = normalizeFieldName(fieldName);

  if (normalized.includes('grupo responsavel')) return 'responsible';
  if (normalized.includes('inicio ocorrencia')) return 'startDate';
  if (normalized.includes('termino ocorrencia')) return 'endDate';
  if (normalized.includes('nome recebedor')) return 'receiverName';
  if (normalized.includes('tipo recebedor')) return 'typeReceiver';
  if (normalized.includes('documento recebedor')) return 'docReceiver';
  if (normalized.includes('entrega atual')) return 'currentDelivery';
  if (normalized.includes('numero documento')) return 'cte';
  if (normalized.includes('numero nf-es')) return 'nfe';
  if (
    normalized.includes('nº ocorrencia cliente') ||
    normalized.includes('no ocorrencia cliente')
  )
    return 'customerNumber';
  if (normalized.includes('a pagar ao transportador')) return 'payCarrier';
  if (normalized.includes('a receber cliente')) return 'receiverClient';
  if (normalized.includes('retido filial')) return 'retainedBranch';
  if (normalized.includes('anexos')) return 'files';
  if (normalized.includes('descricao ocorrencia')) return 'description';

  return null;
}

export function getActiveOccurrenceFields(occurrenceType) {
  const fieldsConfigs = occurrenceType?.occurrenceFieldsConfigs || [];

  // Se não há configuração de campos, retorna objeto indicando modo default
  if (fieldsConfigs.length === 0) {
    return { _hasConfig: false };
  }

  const fields = fieldsConfigs.reduce((acc, fieldConfig) => {
    const statusSlug = fieldConfig?.status?.slug;
    const key = getOccurrenceFieldKey(fieldConfig?.name);

    if (!key || !VISIBLE_STATUSES.includes(statusSlug)) {
      return acc;
    }

    acc[key] = {
      visible: true,
      required: statusSlug === REQUIRED_STATUS,
      name: fieldConfig?.name,
      config: fieldConfig,
    };

    return acc;
  }, {});

  return { ...fields, _hasConfig: true };
}

export function isOccurrenceFieldVisible(activeFields, key) {
  // Se não há configuração, todos os campos dinâmicos são visíveis
  if (activeFields?._hasConfig === false) {
    return true;
  }

  return Boolean(activeFields?.[key]?.visible);
}

export function isOccurrenceFieldRequired(activeFields, key) {
  // Se não há configuração, usar lógica antiga baseada em slug (nenhum campo é obrígatório por configuração)
  if (activeFields?._hasConfig === false) {
    return false;
  }

  return Boolean(activeFields?.[key]?.required);
}

export async function validateEndLotOccurrence(
  occurrenceType,
  description,
  setErrors
) {
  const errorList = {};
  const data = {
    occurrenceType,
    description,
  };
  try {
    await EndLotOccurrenceSchema.validate(data, {
      abortEarly: false,
    });
    setErrors({ ...errorList });
    return true;
  } catch (err) {
    err.inner.forEach((error) => {
      errorList[error.path] = error.message;
    });

    setErrors({ ...errorList });

    return false;
  }
}

export async function validateNewOccurrence(
  occurrenceType,
  occurrenceTypeSecondary,
  responsible,
  startDate,
  endDate,
  description,
  setErrors,
  activeFields,
  nextDeliveryDate
) {
  const errorList = {};
  try {
    if (!occurrenceType?.id) {
      errorList.occurrenceType = 'Tipo de ocorrência obrigatório';
    }

    if (occurrenceType?.children?.length > 0 && !occurrenceTypeSecondary?.id) {
      errorList.occurrenceTypeSecondary = 'Motivo obrigatório';
    }

    // Campos fixos sempre validados
    if (!Array.isArray(responsible) || responsible.length < 1) {
      errorList.responsible = 'Grupo Responsável obrigatório';
    }

    if (Array.isArray(responsible) && responsible.length > 5) {
      errorList.responsible = 'Máximo de 5 grupos';
    }

    if (!startDate) {
      errorList.startDate = 'Forneça uma data válida';
    }

    if (startDate && !parseDateTime(startDate)) {
      errorList.startDate = 'Forneça uma data válida';
    }

    if (endDate && !parseDateTime(endDate)) {
      errorList.endDate = 'Forneça uma data válida';
    }

    // Validação de descrição
    if (activeFields?._hasConfig === false) {
      if (!descriptionNotRequiredSlug.includes(occurrenceType?.slug)) {
        if (!description) {
          errorList.description = 'Descrição obrigatória';
        }
      }

      if (description && description.length > 2000) {
        errorList.description = 'Máximo de 2000 caracteres';
      }
    } else {
      // Validação baseada em configuração dinâmica
      if (isOccurrenceFieldVisible(activeFields, 'description')) {
        if (
          isOccurrenceFieldRequired(activeFields, 'description') &&
          !description
        ) {
          errorList.description = 'Descrição obrigatória';
        }

        if (description && description.length > 2000) {
          errorList.description = 'Máximo de 2000 caracteres';
        }
      }
    }

    if (DELIVERY_ATTEMPT_SLUGS.includes(occurrenceType?.slug)) {
      if (!nextDeliveryDate) {
        errorList.nextDeliveryDate = 'Próxima data de entrega obrigatória';
      } else if (!parseDateTime(nextDeliveryDate)) {
        errorList.nextDeliveryDate = 'Forneça uma data válida';
      }
    }

    if (Object.keys(errorList).length > 0) {
      setErrors({ ...errorList });
      return false;
    }

    setErrors({ ...errorList });
    return true;
  } catch (error) {
    setErrors({
      ...errorList,
      occurrenceType: 'Não foi possível validar os dados da ocorrência',
    });
    return false;
  }
}

export async function validateAttachments(data) {
  const { files } = data;
  try {
    await validateAttachmentsSchema.validate(
      { files },
      {
        abortEarly: false,
      }
    );

    return true;
  } catch (err) {
    return false;
  }
}

export async function validateOccurrenceOboticario(
  occurrenceType,
  data,
  setErrors,
  activeFields
) {
  const errorList = {};
  try {
    if (data?.cte && occurrenceType?.slug === 'SECOND_DELIVERY_ATTEMPT') {
      const hasFirstAttempt = data?.cte?.occurrences.find(
        occurrence => occurrence.occurrenceTypeSlug === 'FIRST_DELIVERY_ATTEMPT'
      );
      if (!hasFirstAttempt) {
        errorList.cteId = `Não existe uma ocorrência do tipo "1° tentativa de entrega" para o mesmo documento`;
      }
    }

    if (!typeOccurrenceByTypeIsValid(data?.cte, occurrenceType)) {
      errorList.cteId = `Já existe uma ocorrência do mesmo tipo (${occurrenceType.name}) para o documento selecionado`;
    }

    // Se não há configuração, usar validação antiga baseada em slug
    if (activeFields?._hasConfig === false) {
      if (attachmentRequired.includes(occurrenceType?.slug)) {
        if (!Array.isArray(data?.files) || data?.files.length < 1) {
          errorList.files = 'Anexo obrigatório para este tipo de ocorrência';
        }
      }

      if (occurrenceType?.slug === 'DELIVERY') {
        if (!data?.receiverName) {
          errorList.receiverName = 'Nome do recebedor obrigatório';
        } else if (data?.receiverName && data?.receiverName.length > 200) {
          errorList.receiverName = 'Máximo de 200 caracteres';
        }

        if (!data?.typeReceiverId) {
          errorList.typeReceiverId = 'Tipo de recebedor obrigatório';
        }

        if (!data?.docReceiver) {
          errorList.docReceiver = 'Documento do recebedor obrigatório';
        } else if (data?.docReceiver) {
          if (!CPF_CNPJ_REGEX.test(data.docReceiver)) {
            errorList.docReceiver = 'Documento deve estar no formato válido de CPF ou CNPJ';
          }
        }

        if (!data?.currentDeliveryId) {
          errorList.currentDeliveryId = 'Entrega atual obrigatório';
        }

        if (!data?.cte?.id) {
          errorList.cteId = 'CT-e obrigatório';
        }

        if (!Array.isArray(data?.nfeIds) || data?.nfeIds.length < 1) {
          errorList.nfeIds = 'NF-e obrigatório';
        }
      } else if (showDocumentFieldsTypes.includes(occurrenceType?.slug)) {
        if (!data?.currentDeliveryId) {
          errorList.currentDeliveryId = 'Entrega atual obrigatório';
        }

        if (!data?.cte?.id) {
          errorList.cteId = 'CT-e obrigatório';
        }

        if (!Array.isArray(data?.nfeIds) || data?.nfeIds.length < 1) {
          errorList.nfeIds = 'NF-e obrigatório';
        }
      }

      if (Object.keys(errorList).length > 0) {
        setErrors({ ...errorList });
        return false;
      }

      setErrors({});
      return true;
    }

    // Validação baseada em configuração dinâmica
    if (isOccurrenceFieldVisible(activeFields, 'receiverName')) {
      if (
        isOccurrenceFieldRequired(activeFields, 'receiverName') &&
        !data?.receiverName
      ) {
        errorList.receiverName = 'Nome do recebedor obrigatório';
      } else if (data?.receiverName && data?.receiverName.length > 200) {
        errorList.receiverName = 'Máximo de 200 caracteres';
      }
    }

    if (
      isOccurrenceFieldVisible(activeFields, 'typeReceiver') &&
      isOccurrenceFieldRequired(activeFields, 'typeReceiver') &&
      !data?.typeReceiverId
    ) {
      errorList.typeReceiverId = 'Tipo de recebedor obrigatório';
    }

    if (isOccurrenceFieldVisible(activeFields, 'docReceiver')) {
      if (
        isOccurrenceFieldRequired(activeFields, 'docReceiver') &&
        !data?.docReceiver
      ) {
        errorList.docReceiver = 'Documento do recebedor obrigatório';
      }

      if (data?.docReceiver) {
        if (!CPF_CNPJ_REGEX.test(data.docReceiver)) {
          errorList.docReceiver = 'Documento deve estar no formato válido de CPF ou CNPJ';
        }
      }
    }

    if (
      isOccurrenceFieldVisible(activeFields, 'currentDelivery') &&
      isOccurrenceFieldRequired(activeFields, 'currentDelivery') &&
      !data?.currentDeliveryId
    ) {
      errorList.currentDeliveryId = 'Entrega atual obrigatório';
    }

    if (
      isOccurrenceFieldVisible(activeFields, 'cte') &&
      isOccurrenceFieldRequired(activeFields, 'cte') &&
      !data?.cte?.id
    ) {
      errorList.cteId = 'CT-e obrigatório';
    }

    if (
      isOccurrenceFieldVisible(activeFields, 'nfe') &&
      isOccurrenceFieldRequired(activeFields, 'nfe')
    ) {
      if (!Array.isArray(data?.nfeIds) || data?.nfeIds.length < 1) {
        errorList.nfeIds = 'NF-e obrigatório';
      }
    }

    if (
      isOccurrenceFieldVisible(activeFields, 'files') &&
      isOccurrenceFieldRequired(activeFields, 'files')
    ) {
      if (!Array.isArray(data?.files) || data?.files.length < 1) {
        errorList.files = 'Anexo obrigatório';
      }
    }

    if (Object.keys(errorList).length > 0) {
      setErrors({ ...errorList });
      return false;
    }

    setErrors({});
    return true;
  } catch (err) {
    setErrors({
      ...errorList,
      cteId: 'Não foi possível validar os campos adicionais da ocorrência',
    });
    return false;
  }
}

export async function validateUpdateOccurrence({
  description,
  startDate,
  endDate,
  setErrors,
  nextDeliveryDate,
  occurrenceTypeSlug,
}) {
  const errorList = {};
  const data = {
    description,
    startDate,
    endDate,
  };
  try {
    await DescriptionSchema.validate(data, {
      abortEarly: false,
    });
  } catch (err) {
    err.inner.forEach((error) => {
      errorList[error.path] = error.message;
    });
  }

  if (DELIVERY_ATTEMPT_SLUGS.includes(occurrenceTypeSlug)) {
    if (!nextDeliveryDate) {
      errorList.nextDeliveryDate = 'Próxima data de entrega obrigatória';
    } else if (!parseDateTime(nextDeliveryDate)) {
      errorList.nextDeliveryDate = 'Forneça uma data válida';
    }
  }

  if (Object.keys(errorList).length > 0) {
    setErrors({ ...errorList });
    return false;
  }

  setErrors({});
  return true;
}

export async function validateAnullOcorrence({ description, setErrors }) {
  const errorList = {};
  const data = {
    description,
  };
  try {
    await AnullSchema.validate(data, {
      abortEarly: false,
    });
    setErrors({ ...errorList });
    return true;
  } catch (err) {
    err.inner.forEach((error) => {
      errorList[error.path] = error.message;
    });
    setErrors({ ...errorList });
    return false;
  }
}

export async function validateChangeResponsible({ description, responsible, setErrors }) {
  const errorList = {};
  const data = {
    description,
    responsible,
  };
  try {
    await ChangeResponsibleSchema.validate(data, {
      abortEarly: false,
    });
    setErrors({ ...errorList });
    return true;
  } catch (err) {
    err.inner.forEach((error) => {
      errorList[error.path] = error.message;
    });

    setErrors({ ...errorList });

    return false;
  }
}

export async function validateAttachmentDescription(fileDescription, setErrors) {
  const errorList = {};
  const data = {
    description: fileDescription,
  };
  try {
    await AttachmentDescriptionSchema.validate(data, {
      abortEarly: false,
    });
    setErrors({ ...errorList });
    return true;
  } catch (err) {
    err.inner.forEach((error) => {
      errorList[error.path] = error.message;
    });

    setErrors({ ...errorList });

    return false;
  }
}

export function validateAttachmentNFLinks(nfes, files) {
  if (!Array.isArray(nfes) || nfes.length === 0) return { valid: true };

  const coveredNfIds = new Set();
  (files || []).forEach(file => {
    (file.selectedNfIds || []).forEach(nfId => coveredNfIds.add(nfId));
  });

  const uncovered = nfes.filter(nf => !coveredNfIds.has(nf.id));

  if (uncovered.length > 0) {
    return {
      valid: false,
      message: `Existem notas fiscais não vinculadas a nenhum anexo: ${uncovered
        .map(nf => nf.nfNumber || String(nf.id))
        .join(', ')}`,
    };
  }

  return { valid: true };
}

export async function onSearchUserResponsible(search) {
  try {
    const response = await api.get('user/all-except-drivers/name', {
      params: { search },
    });
    return response.data;
  } catch (error) {
    return [];
  }
}

export async function onSearchGroups(search) {
  try {
    const { data } = await api.get('groups', {
      params: { search, onlyActive: true },
    });
    return data;
  } catch (error) {
    return [];
  }
}

export function typeOccurrenceByTypeIsValid(document, occurrenceType) {
  if (
    !['FIRST_DELIVERY_ATTEMPT', 'SECOND_DELIVERY_ATTEMPT'].includes(
      occurrenceType?.slug
    )
  ) {
    return true;
  }

  if (!occurrenceType) {
    return true;
  }

  if (!document?.occurrences?.length) {
    return true;
  }

  const hasOccurrenceCreated = document.occurrences.find(
    occurrence => occurrence.occurrenceTypeSlug === occurrenceType?.slug
  );
  if (hasOccurrenceCreated) {
    return false;
  }  return true;
}

export function getFileIcon(ext) {
  if (ext === 'pdf') return { Icon: FaFilePdf, color: '#e74c3c' };
  if (['jpg', 'jpeg', 'png'].includes(ext))
    return { Icon: FaFileImage, color: '#3498db' };
  if (['xls', 'xlsx'].includes(ext))
    return { Icon: FaFileExcel, color: '#27ae60' };
  return { Icon: FaFile, color: '#95a5a6' };
}

export function formatFileSize(bytes) {
  if (!bytes || bytes === 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
